import getLogger from '../logging';

const log = getLogger('control-flow');

/**
 * Execute a given function after the given predicate passes. This can be used
 * to wait on a global variable to be set without guessing how long it will
 * take.
 * @param predicate - a function that should return true when it is safe to run
 * the `then` function.
 * @param then - the function to run when `predicate` is satisfied
 * @param interval - how long to wait between checks of the predicate. Value in
 * milliseconds, defaults to 1ms. Note that this is a minimum amount of time
 * to wait; if there are long running scripts then this doesn't preempt those,
 * it just runs after them.
 * @param timeout - a total length of time to wait for the predicate before
 * giving up and logging an error. Value in milliseconds, defaults to 10s.
 */
export const waitForPredicate = (
    predicate: () => boolean,
    then: () => unknown,
    interval: number = 1,
    timeout: number = 10000): void =>
{
    let hasTimedOut = false;
    const timeoutId = setTimeout(() => { hasTimedOut = true; }, timeout);
    const maybeRetry = () => {
        if(hasTimedOut) {
            log.error('Timeout of awaited predicate', predicate, then);
        }
        // If either predicate() or then() raise an exception, we will not
        // retry again. This in fine in either case.
        else if(predicate()) {
            clearTimeout(timeoutId);
            then();
        } else {
            setTimeout(maybeRetry, interval);
        }
    };

    try {
        // If an exception bubbles up to us on the first attempt, then we log
        // it and do not retry. I'm swallowing the exception here so that this
        // doesn't otherwise blow up an initialization procedure where this is
        // likely to be used.
        maybeRetry();
    } catch(error) {
        log.error(error);
    }
};
export default waitForPredicate;
